import { isEmpty, isNil, omit } from 'lodash';

import { RegUser } from '@common/types';
import { SessionStorage } from '@services/storage/storage';
import { TermAddOn, TermQuotePlanDetails } from '@store/termquote';
import {
  ACCIDENTAL_DEATH,
  CRITICAL_ILLNESS,
  INCOME_BENEFIT,
  LIFE_STAGE_BENEFIT,
  WAIVER_OF_PREMIUM,
  ACCIDENTAL_TOTAL_PERMANENT_DISABILITY,
  ACCIDENTAL_COVER
} from '@termquote/constants';
import { dayjs } from '@ui';
import { TERM_INSURANCE_PRODUCTS } from '@utils/constants';

import {
  ProductFeaturesRequest,
  TermAddOnsRequest,
  TermCoverSuggestionRequest,
  TermEstimateRequest,
  TermPayOptionsRequest,
  TermPremiumRequest,
  TermQuoteFinalizeRequest
} from './types';

export const suggestionTransform = (
  {
    salaried,
    education,
    dob,
    gender,
    tobacco,
    diabetic,
    annInc,
    mExpenses,
    pincode,
    product,
    isNRI,
    nriCountry,
    gstWaiver,
    existingCustomer,
    firstTimeBuyer,
    isExistingUser
  }: TermQuotePlanDetails,
  quote?: string
): TermCoverSuggestionRequest => {
  return {
    annInc: annInc ?? 0,
    mExpenses: mExpenses ?? 0,
    pincode: pincode ? pincode : undefined,
    salaried: salaried ?? false,
    tobacco: tobacco ?? false,
    diabetic: diabetic ?? false,
    dob: dob ? dayjs(dob).format('YYYY-MM-DD') : '',
    education: education ?? '',
    gender: gender ?? '',
    products: product && quote === 'fast' ? [product] : undefined,
    is_nri: isNRI ?? false,
    ...(isNRI ? { nri_country: nriCountry } : {}),
    gst_waiver: gstWaiver ?? false,
    // Should be sent when existingUsersOnly is true in product boundary
    is_existing_user: isExistingUser,
    ...(!isNil(existingCustomer) && { existing_customer: existingCustomer }), // existingCustomer is used only for BALIC - so unless present its excluded
    ...(!isNil(firstTimeBuyer) && { first_time_buyer: firstTimeBuyer }) // firstTimeBuyer is used only for BALIC - so unless present its excluded
  };
};

export const estimateTransform = (
  plan: TermQuotePlanDetails,
  quote?: string
): TermEstimateRequest => ({
  ...suggestionTransform(plan, quote),
  cover: plan.cover,
  paymentFreq: plan.paymentFreq,
  tillAge: plan.tillAge
});

export const payoptionsTransform = (
  plan: TermQuotePlanDetails
): TermPayOptionsRequest => ({
  ...estimateTransform(plan),
  product: plan.product,
  ...(plan.productVariant ? { [plan.productVariant]: true } : {})
});

export const addOnsTransform = (plan: TermQuotePlanDetails): TermAddOnsRequest => ({
  ...payoptionsTransform(plan),
  paymentType: plan.payOption
});

export const productFeaturesTransform = (
  products: string[],
  { dob, cover, tillAge, isNRI, gstWaiver, payOption }: TermQuotePlanDetails
): ProductFeaturesRequest => ({
  products,
  dob: !isEmpty(dob) ? dayjs(dob).format('YYYY-MM-DD') : undefined,
  tillAge: tillAge ? tillAge : undefined,
  cover: cover ? cover : undefined,
  is_nri: isNRI,
  gst_waiver: gstWaiver,
  paymentType: !isEmpty(payOption) ? payOption : undefined
});

const getWopEligibility = (
  plan: TermQuotePlanDetails,
  includedAddOns: string[],
  addonsInfo: Pick<
    TermPremiumRequest,
    | typeof ACCIDENTAL_DEATH
    | typeof ACCIDENTAL_TOTAL_PERMANENT_DISABILITY
    | typeof CRITICAL_ILLNESS
  >,
  currentAddOn: string
): boolean => {
  const isIpru = plan.product === TERM_INSURANCE_PRODUCTS.I_PROTECT_SMART_PLUS;
  if (!isIpru) return true;
  if (includedAddOns.includes(WAIVER_OF_PREMIUM)) return true;

  const otherPaidAddons = [
    ACCIDENTAL_DEATH,
    ACCIDENTAL_TOTAL_PERMANENT_DISABILITY,
    CRITICAL_ILLNESS
  ] as const;
  const selectedOtherPaidAddonCovers = otherPaidAddons.some(
    (addOn) => (addonsInfo[addOn as keyof typeof addonsInfo] ?? 0) > 0
  );

  return currentAddOn === WAIVER_OF_PREMIUM && !selectedOtherPaidAddonCovers;
};

export const termPremiumTransform = (
  plan: TermQuotePlanDetails,
  addOns: TermAddOn[],
  currentAddOn: string
): TermPremiumRequest => {
  const addOnsInfo: Partial<TermPremiumRequest> = {};
  const includedAddOns: string[] = [];
  addOns.forEach((addOn) => {
    /**
     * Check addOn is eligible and a cover is selected
     * in case of wop always send true since we need
     * to show the addOn premium always.
     */
    if (addOn.key === ACCIDENTAL_DEATH) {
      addOnsInfo.adhb = addOn.isEligible ? addOn.cover ?? 0 : 0;
    } else if (addOn.key === ACCIDENTAL_COVER) {
      addOnsInfo.acb = addOn.isEligible ? addOn.cover ?? 0 : 0;
    } else if (addOn.key === ACCIDENTAL_TOTAL_PERMANENT_DISABILITY) {
      addOnsInfo.atpd = addOn.isEligible ? addOn.cover ?? 0 : 0;
    } else if (addOn.key === CRITICAL_ILLNESS) {
      addOnsInfo.ci = addOn.isEligible ? addOn?.cover ?? 0 : 0;
      addOnsInfo.ci_details = addOn.isEligible ? addOn?.selectedVariant : undefined;
    } else if (addOn.key === WAIVER_OF_PREMIUM) {
      addOnsInfo.wop = addOn.isEligible
        ? getWopEligibility(plan, includedAddOns, addOnsInfo, currentAddOn)
        : false;
    } else if (addOn.key === INCOME_BENEFIT) {
      addOnsInfo.ib = addOn.isEligible ? addOn.cover ?? 0 : 0;
    }
    if (!addOn.isFree && addOn.included) {
      includedAddOns.push(addOn.key);
    }
    // todo: check why we are not doing this for free optional addOns
    // else if (addOn.isFree && addOn.isOptional && addOn.included) {
    //   includedAddOns.push(addOn.key);
    // }
  });
  return {
    ...addOnsTransform(plan),
    ...addOnsInfo,
    includedRiders: includedAddOns
  };
};

export const termFinalizeTransform = (
  plan: TermQuotePlanDetails,
  addOns: TermAddOn[]
): TermPremiumRequest => {
  const addOnsInfo: Partial<TermPremiumRequest> = {};
  const includedAddOns: string[] = [];
  addOns.forEach((addOn) => {
    /**
     * Check addOn is eligible,included and a cover is selected
     */
    if (addOn.key === ACCIDENTAL_DEATH) {
      addOnsInfo.adhb = addOn.isEligible && addOn.included ? addOn.cover ?? 0 : 0;
    } else if (addOn.key === ACCIDENTAL_COVER) {
      addOnsInfo.acb = addOn.isEligible && addOn.included ? addOn.cover ?? 0 : 0;
    } else if (addOn.key === ACCIDENTAL_TOTAL_PERMANENT_DISABILITY) {
      addOnsInfo.atpd = addOn.isEligible && addOn.included ? addOn.cover ?? 0 : 0;
    } else if (addOn.key === CRITICAL_ILLNESS) {
      addOnsInfo.ci = addOn.isEligible && addOn.included ? addOn?.cover ?? 0 : 0;
      addOnsInfo.ci_details =
        addOn.isEligible && addOn.included ? addOn?.selectedVariant : undefined;
    } else if (addOn.key === WAIVER_OF_PREMIUM) {
      addOnsInfo.wop = addOn.isEligible && addOn.included ? true : false;
    } else if (addOn.key === INCOME_BENEFIT) {
      addOnsInfo.ib = addOn.isEligible && addOn.included ? addOn.cover ?? 0 : 0;
    } else if (addOn.key === LIFE_STAGE_BENEFIT) {
      addOnsInfo.lsb = addOn.isEligible && addOn.included ? true : false;
    }
    if (!addOn.isFree && addOn.included) {
      includedAddOns.push(addOn.key);
    }
  });
  return {
    ...addOnsTransform(plan),
    ...addOnsInfo,
    includedRiders: includedAddOns
  };
};

export const finalizeTransform = (
  plan: TermQuotePlanDetails,
  addOns: TermAddOn[],
  user: RegUser,
  otpId?: string
): TermQuoteFinalizeRequest => {
  const team = SessionStorage?.get('team');
  return {
    product_details: {
      ...omit(termFinalizeTransform(plan, addOns), ['includedRiders'])
    },
    ...(!isEmpty(team) && {
      other_details: {
        team: team!
      }
    }),
    user: {
      phone_num: user.mobile,
      country_code: user.country_code,
      otp: user.otp,
      // Full name will be there when finalizing the quote
      full_name: user.fullName!
    },
    ...(otpId && { otp_id: otpId })
  };
};
